<template>
  <div class="flex items-start gap-4">
    <button
      type="button"
      class="relative flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-card p-2 transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
      :class="isDraggingRef ? 'border-2 border-dashed border-primary bg-accent' : modelValue ? '' : 'border-dashed'"
      :aria-label="
        modelValue ? t('admin.setup.branding.logo.replace') : (chooseLabel ?? t('admin.setup.branding.logo.choose'))
      "
      :aria-describedby="`${id}-hint`"
      :disabled="isUploadingRef"
      :data-test-id="`${id}-dropzone`"
      @click="openPicker"
      @dragover.prevent="isDraggingRef = true"
      @dragleave.prevent="isDraggingRef = false"
      @drop.prevent="onDrop"
    >
      <Spinner v-if="isUploadingRef" class="size-5" aria-hidden="true" />
      <img
        v-else-if="modelValue"
        :src="modelValue"
        alt=""
        class="h-auto! max-h-full w-auto! max-w-full object-contain!"
      />
      <template v-else-if="fallbackSrc">
        <img :src="fallbackSrc" alt="" class="h-auto! max-h-[70%] w-auto! max-w-full object-contain!" />
        <span
          class="absolute inset-x-0 bottom-0 bg-muted py-0.5 text-center text-xs font-semibold text-muted-foreground"
        >
          {{ t('admin.setup.branding.logo.default') }}
        </span>
      </template>
      <ImagePlusIcon v-else class="size-6 text-muted-foreground" aria-hidden="true" />
    </button>

    <div class="flex min-w-0 flex-1 flex-col gap-2">
      <div class="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          :disabled="isUploadingRef"
          :data-test-id="`${id}-choose`"
          @click="openPicker"
        >
          <UploadIcon class="size-4" aria-hidden="true" />
          {{
            modelValue ? t('admin.setup.branding.logo.replace') : (chooseLabel ?? t('admin.setup.branding.logo.choose'))
          }}
        </Button>
        <Button
          v-if="modelValue"
          variant="ghost"
          size="sm"
          :disabled="isUploadingRef"
          :data-test-id="`${id}-remove`"
          @click="emit('update:modelValue', '')"
        >
          {{ removeLabel ?? t('admin.setup.branding.logo.remove') }}
        </Button>
      </div>
      <p :id="`${id}-hint`" class="text-xs leading-relaxed text-muted-foreground">
        {{ description ?? t('admin.setup.branding.logo.description') }}
      </p>
      <p v-if="isUploadingRef" role="status" class="text-xs text-muted-foreground">
        {{ t('admin.setup.branding.logo.uploading') }}
      </p>
      <p v-if="errorRef" role="alert" class="text-xs text-destructive" :data-test-id="`${id}-error`">{{ errorRef }}</p>
    </div>

    <input
      :id="id"
      ref="inputRef"
      type="file"
      class="sr-only"
      tabindex="-1"
      aria-hidden="true"
      :accept="ACCEPTED_TYPES.join(',')"
      :data-test-id="`${id}-input`"
      @change="onPick"
    />
  </div>
</template>

<script setup lang="ts">
import { ImagePlusIcon, UploadIcon } from 'lucide-vue-next'
import { ref } from 'vue'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { uploadPlatformLogo } from '@/features/admin/platform-setup/api'
import { useI18n } from '@/lib/i18n'

// Mêmes règles que l'API (api/utils/platformSetup.ts) : pas de SVG, 2 Mo maximum.
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const MAX_BYTES = 2 * 1024 * 1024

defineProps<{
  modelValue: string
  id: string
  description?: string
  /** Image affichée tant qu'aucun logo n'est choisi (ex. le logo de la plateforme pour le bouton SSO). */
  fallbackSrc?: string
  chooseLabel?: string
  removeLabel?: string
}>()
const emit = defineEmits<{ 'update:modelValue': [url: string] }>()

const { t } = useI18n()

const inputRef = ref<HTMLInputElement | null>(null)
const isUploadingRef = ref(false)
const isDraggingRef = ref(false)
const errorRef = ref('')

function openPicker() {
  inputRef.value?.click()
}

function onPick(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  // Remis à zéro pour pouvoir choisir de nouveau le même fichier.
  input.value = ''
  if (file) void upload(file)
}

function onDrop(event: DragEvent) {
  isDraggingRef.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file) void upload(file)
}

async function upload(file: File) {
  errorRef.value = ''
  if (!ACCEPTED_TYPES.includes(file.type)) {
    errorRef.value = t('admin.setup.branding.logo.errors.format')
    return
  }
  if (file.size > MAX_BYTES) {
    errorRef.value = t('admin.setup.branding.logo.errors.size')
    return
  }
  isUploadingRef.value = true
  try {
    const { url } = await uploadPlatformLogo(file)
    emit('update:modelValue', url)
  } catch {
    errorRef.value = t('admin.setup.branding.logo.errors.upload')
  } finally {
    isUploadingRef.value = false
  }
}
</script>
